import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient, type CreateUserRequestDto, type UserDto } from '@/api/client';
import { queryKeys } from '@/query/keys';

import {
  adminUsersEndpoints,
  type UpdateUserProfileBody,
  type UserDemographicsDto,
} from './endpoints';

export const DEFAULT_USERS_PAGE_SIZE = 10;

export interface AdminUserViewModel {
  readonly uuid: string;
  readonly name: string;
  readonly fullName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly status: string | null;
  readonly tenantUuid: string | null;
  readonly createdOn: string | null;
  readonly roles: readonly string[];
}

export interface AdminUsersPageViewModel {
  readonly rows: readonly AdminUserViewModel[];
  /** 0-based page number that the BE returned. */
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

export function mapAdminUser(dto: UserDto): AdminUserViewModel {
  const name = (dto.name ?? '').trim();
  // BE serves `fullName` as the human-readable name (alongside `name` =
  // username). Mock data sometimes only sets `name` to a full name; fall
  // back so the firstName/lastName columns stay populated either way.
  const fullName = (dto.fullName ?? '').trim() || name;
  const [firstName, lastName] = splitFullName(fullName);
  return {
    uuid: dto.uuid ?? '',
    name,
    fullName,
    firstName,
    lastName,
    email: (dto.email ?? '').trim(),
    status: nonEmpty(dto.status),
    tenantUuid: nonEmpty(dto.tenantUUID),
    createdOn: nonEmpty(dto.createdOn),
    roles: Array.isArray(dto.roles) ? dto.roles : [],
  };
}

function splitFullName(value: string): readonly [string, string] {
  const trimmed = value.trim();
  if (trimmed.length === 0) return ['', ''];
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return [trimmed, ''];
  return [trimmed.slice(0, idx), trimmed.slice(idx + 1).trim()];
}

function nonEmpty(value: string | undefined | null): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export interface AdminUsersQueryParams {
  readonly page: number;
  readonly size: number;
  /** Trimmed search query. Empty string falls back to the unfiltered list endpoint. */
  readonly q?: string;
}

export function adminUsersQueryKey(params: AdminUsersQueryParams): readonly unknown[] {
  return ['admin', 'users', params.page, params.size, (params.q ?? '').trim()];
}

/**
 * Switches between `GET /users` and `GET /users/search` based on whether `q` is
 * non-empty. `keepPreviousData` keeps the table populated across page/query
 * transitions so the UI doesn't flash a skeleton on every keystroke.
 */
export function useAdminUsers(
  params: AdminUsersQueryParams,
): UseQueryResult<AdminUsersPageViewModel> {
  return useQuery<AdminUsersPageViewModel>({
    queryKey: adminUsersQueryKey(params),
    queryFn: async ({ signal }) => {
      const trimmed = (params.q ?? '').trim();
      const page = trimmed.length > 0
        ? await apiClient.searchUsers(
            { q: trimmed, page: params.page, size: params.size },
            { signal },
          )
        : await apiClient.getUsers({ page: params.page, size: params.size }, { signal });
      const rows = page.content.map(mapAdminUser);
      return {
        rows,
        page: page.number ?? params.page,
        size: page.size ?? params.size,
        totalElements: page.totalElements ?? rows.length,
        totalPages: page.totalPages ?? Math.max(1, Math.ceil((page.totalElements ?? rows.length) / Math.max(params.size, 1))),
      };
    },
    placeholderData: keepPreviousData,
  });
}

export interface CreateAdminUserInput {
  readonly name: string;
  readonly email: string;
  readonly status?: string | null;
}

export function useAdminUserMutations(): {
  readonly create: UseMutationResult<AdminUserViewModel, Error, CreateAdminUserInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }).catch(() => undefined);
  };

  const create = useMutation<AdminUserViewModel, Error, CreateAdminUserInput>({
    meta: { successMessage: 'success.toast.title.user_created' },
    mutationFn: async (input) => {
      const body: CreateUserRequestDto = {
        name: input.name,
        email: input.email,
        ...(input.status !== undefined && input.status !== null && input.status.length > 0
          ? { status: input.status }
          : {}),
      };
      const dto = await apiClient.createUser(body);
      return mapAdminUser(dto);
    },
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    meta: { successMessage: 'success.toast.title.user_deleted' },
    mutationFn: (uuid) => apiClient.deleteUser(uuid),
    onSettled: invalidate,
  });

  return { create, remove };
}

/* ------------------------------------------------------------------ */
/* Editable profile (demographics) — shared by the tenant + platform user pages */
/* ------------------------------------------------------------------ */

/** The user page's editable name fields. */
export interface UserProfileDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly title: string;
}

/** Normalise the wire demographics to the editable draft. */
export function mapDemographics(dto: UserDemographicsDto): UserProfileDraft {
  return {
    firstName: dto.first_name ?? '',
    lastName: dto.last_name ?? '',
    title: dto.title ?? '',
  };
}

/** Build the update body from a draft (title cleared → explicit null so the server unsets it). */
export function toProfileBody(draft: UserProfileDraft): UpdateUserProfileBody {
  const title = draft.title.trim();
  return {
    first_name: draft.firstName.trim(),
    last_name: draft.lastName.trim(),
    title: title.length > 0 ? title : null,
  };
}

/** Cache key for a user's demographics sub-resource. */
export function userDemographicsKey(uuid: string): readonly unknown[] {
  return ['users', uuid, 'demographics'];
}

export interface UpdateUserProfileInput {
  readonly uuid: string;
  readonly draft: UserProfileDraft;
}

/** A single TENANT user by uuid (identity summary) for the user page. */
export function useAdminUser(uuid: string): UseQueryResult<AdminUserViewModel> {
  return useQuery<AdminUserViewModel>({
    queryKey: queryKeys.user(uuid),
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) => mapAdminUser(await apiClient.getUser(uuid, { signal })),
  });
}

/** A TENANT user's assigned role names (for the user page's top-bar chips). */
export function useAdminUserRoles(uuid: string): UseQueryResult<readonly string[]> {
  return useQuery<readonly string[]>({
    queryKey: ['users', uuid, 'roles'],
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) =>
      (await adminUsersEndpoints.getUserRoles(uuid, { signal })).roleNames ?? [],
  });
}

/** A TENANT user's editable demographics (first/last/title). */
export function useAdminUserDemographics(uuid: string): UseQueryResult<UserProfileDraft> {
  return useQuery<UserProfileDraft>({
    queryKey: userDemographicsKey(uuid),
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) =>
      mapDemographics(await adminUsersEndpoints.getUserDemographics(uuid, { signal })),
  });
}

/** Update a TENANT user's profile (self or admin — guarded server-side). */
export function useUpdateAdminUserProfile(): UseMutationResult<undefined, Error, UpdateUserProfileInput> {
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, UpdateUserProfileInput>({
    mutationFn: async ({ uuid, draft }) => {
      await adminUsersEndpoints.updateUserProfile(uuid, toProfileBody(draft));
      return undefined;
    },
    onSettled: (_data, _err, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(uuid) }).catch(() => undefined);
      queryClient.invalidateQueries({ queryKey: userDemographicsKey(uuid) }).catch(() => undefined);
      // A self-edit changes /me, so the avatar/name reflects it immediately.
      queryClient.invalidateQueries({ queryKey: ['me'] }).catch(() => undefined);
    },
  });
}
